import {SignalStore, SignalStoreConfig, SignalStoreType} from './signal-store';

export type StateEntity<S extends object & {id: string} = object & {id: string}, K extends keyof S = 'id'> = S & {
	[key in K]: unknown;
};

export type EntityMap<Entity extends StateEntity> = Record<string, Entity>;

export type GlobalState = Exclude<Record<string, any>, 'Entities'>;

export type EntityState<E extends StateEntity, G extends GlobalState = GlobalState> = G & {
	Entities: {
		entityMap: EntityMap<E>;
		ids: string[];
	};
};

export abstract class EntitySignalStore<TStateEntity extends StateEntity, GState extends GlobalState = GlobalState> extends SignalStore<EntityState<TStateEntity, GState>> {
	protected constructor(
		name: string,
		type: SignalStoreType,
		private entityIdKey: Extract<keyof TStateEntity, string> = 'id' as Extract<keyof TStateEntity, string>,
		config?: Partial<SignalStoreConfig<EntityState<TStateEntity, GState>>>,
		private initialGlobalState: GState = {} as GState,
	) {
		const initialState: EntityState<TStateEntity, GState> = {
			...initialGlobalState,
			Entities: {
				entityMap: {},
				ids: [],
			},
		};
		super(name, type, initialState, config);
	}

	public beforeAddEntity?(entity: TStateEntity): void;

	public afterAddEntity?(entity: TStateEntity): void;

	protected getEntity(id: string): TStateEntity | undefined {
		return this.state().Entities.entityMap?.[id];
	}

	protected getEntityIds(): string[] {
		return this.state().Entities.ids;
	}

	protected hasEntity(id: string): boolean {
		return !!this.state().Entities.entityMap?.[id];
	}

	protected hasEntities(): boolean {
		return this.state().Entities.ids.length > 0;
	}

	protected addEntity(entity: TStateEntity): void {
		this.beforeAddEntity?.(entity); // קריאה ל-Hook לפני הוספה
		const id = entity[this.entityIdKey] as string;
		const stateIds = new Set(this.state().Entities.ids);
		stateIds.add(id);
		this.update(
			state => ({
				...state,
				Entities: {
					entityMap: {
						...state.Entities.entityMap,
						[id]: entity,
					},
					ids: [...stateIds],
				},
			}),
			`Add Entity (${id})`,
		);
		this.afterAddEntity?.(entity); // קריאה ל-Hook אחרי הוספה
	}

	protected addEntities(entities: TStateEntity[]): void {
		entities.forEach(entity => this.addEntity(entity));
	}

	protected updateEntity(id: string, updateFn: (entity: TStateEntity) => TStateEntity): void {
		this.update(
			state => ({
				...state,
				Entities: {
					entityMap: {
						...state.Entities.entityMap,
						[id]: updateFn(state.Entities.entityMap[id] || ({} as TStateEntity)),
					},
					ids: [...state.Entities.ids, id],
				},
			}),
			`Update Entity (${id})`,
		);
	}

	protected updateEntities(newEntities: TStateEntity[], updateFn: (stateEntity: TStateEntity, newEntity: TStateEntity) => TStateEntity): void {
		newEntities.forEach(newEntity => {
			this.updateEntity(newEntity.id, stateEntity => updateFn(stateEntity, newEntity));
		});
	}

	protected removeEntity(id: string): void {
		this.update(
			state => ({
				...state,
				Entities: {
					entityMap: {
						...state.Entities.entityMap,
						[id]: undefined,
					},
					ids: state.Entities.ids.filter(id => id !== id),
				},
			}),
			`Remove Entity (${id})`,
		);
	}

	protected removeEntities(ids: string[]): void {
		ids.forEach(id => this.removeEntity(id));
	}

	protected clearEntities(): void {
		this.update(
			() => ({
				...this.initialGlobalState,
				Entities: {
					entityMap: {},
					ids: [],
				},
			}),
			'Clear Entities',
		);
	}
}
